import { Button } from "@/components/ui/button";
import { useApi } from "@/hooks/use-api";
import { useAppState } from "@/store/useApp";
import { AssessmentInfo } from "@/types/assessment";
import { Course } from "@/types/course";
import { Archive, Edit } from "lucide-react";
import { useState } from "react";
import OnboardingManageAssessmentsHint from "../../reader/ onboarding/flows/OnboardingManageAssessmentsHint";
import { calculateAssessmentStatus } from "../course-service";
import { CourseHeader } from "./CourseHeader";
import { AssessmentList } from "./assessments/AssessmentList";
import { PublishAssessmentDialog } from "./assessments/PublishAssessmentDialog";
import { RenameAssessmentDialog } from "./assessments/RenameAssessmentDialog";
import { ReportTypeDialog } from "./assessments/ReportTypeDialog";
import { AssessmentWizard } from "./assessments/wizard/AssessmentWizard";
import { Badge } from "@/components/ui/badge";

interface OverviewProps {
  course: Course;
  studentCount: number;
  onEdit: () => void;
}

export const Overview = ({
  course,
  studentCount,
  onEdit,
}: OverviewProps) => {
  const assessments = useAppState((state) => state.assessments || []);
  const documents = useAppState((state) => state.documents || []);
  const { deleteAssessment, exportGradingReport, patchAssessment } = useApi();

  const [assessmentToRename, setAssessmentToRename] =
    useState<AssessmentInfo | null>(null);
  const [reportTypeDialogOpen, setReportTypeDialogOpen] = useState(false);
  const [assessmentToExport, setAssessmentToExport] =
    useState<AssessmentInfo | null>(null);
  const [hasAssessmentDocuments, setHasAssessmentDocuments] = useState(false);
  const [hasIncompleteSubmissions, setHasIncompleteSubmissions] =
    useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [assessmentToPublish, setAssessmentToPublish] =
    useState<AssessmentInfo | null>(null);

    const filteredAssessments = assessments.filter(
    (assessment) => assessment.courseId === course.id
  );

  const handleRenameAssessment = (assessment: AssessmentInfo) => {
    setAssessmentToRename(assessment);
  };

  const handleDeleteAssessment = async (assessmentId: string) => {
    await deleteAssessment(course.id, assessmentId);
  };

  const handleDownloadAssessment = (assessment: AssessmentInfo) => {
    const status = calculateAssessmentStatus(assessment);
    const assessmentDocuments = documents.filter(
      (doc) => doc.assessmentId === assessment.id
    );
    setAssessmentToExport(assessment);
    setHasAssessmentDocuments(assessmentDocuments.length > 0);
    setHasIncompleteSubmissions(status !== "completed");
    setReportTypeDialogOpen(true);
  };

  const handleReportDialogClose = (isOpen: boolean) => {
    setReportTypeDialogOpen(isOpen);
    if (!isOpen) {
      setAssessmentToExport(null);
      setHasAssessmentDocuments(false);
    }
  };

  const handlePublishAssessment = (assessment: AssessmentInfo) => {
    setAssessmentToPublish(assessment);
    setPublishDialogOpen(true);
  };

  const headerActions = (
    <>
      <Button
        size="sm"
        onClick={() => setIsCreating(true)}
      >
        New assessment
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={onEdit}
        aria-label="Edit course"
      >
        <Edit className="h-4 w-4" />
      </Button>
      <Button variant="ghost" size="icon" aria-label="Archive course">
        <Archive className="h-4 w-4" />
      </Button>
    </>
  );

  if (isCreating) {
    return (
      <AssessmentWizard
        courseId={course.id}
        onCancel={() => setIsCreating(false)}
        onComplete={() => setIsCreating(false)}
      />
    );
  }

  return (
    <>
      <CourseHeader
        title="Overview"
        courseTitle={course.title}
        actions={headerActions}
        info={
          <>
            <div>
              <span className="text-muted-foreground">Semester: </span>
              <span className="font-medium bg-brand text-white px-1.5 py-0.5 rounded text-xs">
                {course.semester || course.reference}
              </span>
            </div>
          <div>
            <span className="text-muted-foreground">Students enrolled: </span>
            <Badge variant="secondary">{studentCount}</Badge>
          </div>
          </>
        }
      />

      <div className="mt-10 flex flex-col items-center justify-between w-full">
        <div className="w-full max-w-5xl">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium">Assessments</h3>
          </div>

          {filteredAssessments.length === 0 ? (
            <>
              <div className="flex flex-col items-center h-[calc(100vh-600px)] justify-center mx-auto border border-dashed rounded-lg">
                <span className="font-medium">
                  This course has no assessments
                </span>
                <p className="text-muted-foreground text-xs">
                  Create your first assessment
                </p>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center w-full">
              <AssessmentList
                assessments={filteredAssessments}
                onEditAssessment={handleRenameAssessment}
                onDeleteAssessment={handleDeleteAssessment}
                onDownloadAssessment={handleDownloadAssessment}
                onPublishAssessment={handlePublishAssessment}
              />

              <OnboardingManageAssessmentsHint />
            </div>
          )}
        </div>
      </div>

      <RenameAssessmentDialog
        assessment={assessmentToRename}
        open={!!assessmentToRename}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setAssessmentToRename(null);
          }
        }}
        onRename={async (assessmentId, newTitle) => {
          await patchAssessment(course.id, assessmentId, { title: newTitle });
        }}
      />

      <ReportTypeDialog
        open={reportTypeDialogOpen}
        onOpenChange={handleReportDialogClose}
        assessmentId={assessmentToExport?.id || null}
        exportGradingReport={exportGradingReport}
        hasAssessmentDetails={hasAssessmentDocuments}
        hasIncompleteSubmissions={hasIncompleteSubmissions}
      />

      <PublishAssessmentDialog
        courseId={course.id}
        open={publishDialogOpen}
        onOpenChange={(open) => {
          setPublishDialogOpen(open);
          if (!open) setAssessmentToPublish(null);
        }}
        assessment={assessmentToPublish || undefined}
        disableAssessmentSelect={!!assessmentToPublish}
      />
    </>
  );
};
